// The Sequence tab (GRO-287): pick this account's people, build the steps, let Scout draft
// each one once for everyone in the strategist's voice, then create it in Amplemarket. Edits
// save on their own. Shared copy uses {{first_name}}, {{company_name}} and {{personal_line}};
// Create waits until every person has every field the copy uses.
//
// "Create draft in Amplemarket" makes a draft only. "Create and launch in Amplemarket" creates,
// then, only if the create finished in full, asks in a confirm dialog naming the sequence, the
// people the create actually added and who it left out, and launches on OK: the one launch path
// (`launchSequenceInAmplemarket`). Cancel leaves a created draft. Creation runs create → steps →
// leads and records how far it got, so a half-finished create retries from there.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSequenceInAmplemarket, disconnectSequenceConnection, draftSequencePersonalLine, draftSequenceStep,
  fetchSequenceConnection, fetchSequenceDrafts, fetchSequenceMailboxes, launchSequenceInAmplemarket, panelMessage,
  refreshSequenceTools, saveSequenceDraft, startSequenceConnection,
} from '../api/client';
import type { TokenGetter } from '../api/client';
import type {
  BookAccount, SequenceConnection, SequenceCreateOutcome, SequenceCreation, SequenceDraft, SequenceLaunchOutcome,
  SequenceMissingField, SequenceStepType,
} from '../api/types';
import {
  PERSONAL_LINE_FIELD, STEP_TYPES, addStep, builderAfterSave, builderFromDraft, contactBlocker, contactHasName, contactNameHint,
  copyUsesField, createBlockers, createMode, personalLinesStillEmpty, withSeededContacts,
  createOutcomeUnknown, creationHeadline, moveStep, personalLinesToFill, personalLinesWithLiquid,
  previewCreateNote, builderContacts, removeField, removeStep, renameBuilder, savePayload, seededBuilder, setCampaignType,
  launchConfirmText, launchHeadline, launchLeadCount, setPersonalLine, stepDays, toggleContact, updateStep, usableMailboxes,
} from '../lib/sequenceBuilder';
import { fillLinesWithRetry } from '../lib/personalLineFill';
import { autosaveReady, emptyTracker, flushPendingSave, trackSave, untrackSave } from '../lib/sequenceAutosave';
import { CAMPAIGN_TYPE_GROUPS, prefixedName, unknownPrefix } from '../lib/campaignTypes';
import { contactDisplay, contactDisplayName, isSharedInbox } from '../lib/contactName';
import type { BuilderState } from '../lib/sequenceBuilder';
import { SideBlock } from './blocks';
import { SequenceFieldCheck } from './SequenceFieldCheck';
import { SequenceMailboxPicker } from './SequenceMailboxPicker';
import { SequencePersonalLines } from './SequencePersonalLines';
import { SequenceStepCard } from './SequenceStepCard';

// How long the builder waits after the last edit before saving the draft on its own.
const AUTOSAVE_DELAY_MS = 1200;

export function SequenceTab({ row, getToken, active, seedContactIds = [] }: {
  row: BookAccount;
  getToken: TokenGetter;
  active: boolean;
  // People picked on Outreach before "New sequence": a fresh builder starts with them, in place
  // of reopening the newest saved draft.
  seedContactIds?: string[];
}) {
  const queryClient = useQueryClient();
  const connectionQuery = useQuery({
    queryKey: ['sequence-connection'],
    queryFn: () => fetchSequenceConnection(getToken),
    enabled: active,
  });
  const draftsQuery = useQuery({
    queryKey: ['sequence-drafts', row.id],
    queryFn: () => fetchSequenceDrafts(getToken, row.id),
    enabled: active,
  });

  const connection = connectionQuery.data ?? null;
  const isDryRun = createMode(connection) === 'dry_run';
  const mailboxesQuery = useQuery({
    queryKey: ['sequence-mailboxes'],
    queryFn: () => fetchSequenceMailboxes(getToken),
    enabled: active && Boolean(connection?.connected) && !isDryRun,
  });

  const [builder, setBuilder] = useState<BuilderState>(() => seededBuilder(row.contacts, seedContactIds));
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [draftingStepId, setDraftingStepId] = useState<string | null>(null);
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'save' | 'create' | 'launch' | 'connect' | 'tools' | 'fill' | null>(null);
  const [fillingContactId, setFillingContactId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: string; text: string } | null>(null);
  const [outcome, setOutcome] = useState<SequenceCreateOutcome | null>(null);
  const [pickedMailbox, setPickedMailbox] = useState<string | null>(null);
  const [launchOutcome, setLaunchOutcome] = useState<SequenceLaunchOutcome | null>(null);
  // The builder state whose autosave failed: not retried until the next edit or "Retry save".
  const [failedSave, setFailedSave] = useState<BuilderState | null>(null);

  // The builder as of the last render, for an async step that must start from the newest state.
  const latestBuilder = useRef(builder);
  latestBuilder.current = builder;

  // People newly ticked in Outreach since the last seed arrive picked. Only the new ones: someone
  // un-picked here stays un-picked while still ticked there. Keyed on the ids, not the array.
  const seededIds = useRef(seedContactIds);
  const seedKey = seedContactIds.join(',');
  useEffect(() => {
    const newIds = seedContactIds.filter(contactId => !seededIds.current.includes(contactId));
    seededIds.current = seedContactIds;
    if (newIds.length) setBuilder(state => withSeededContacts(state, row.contacts, newIds));
  }, [seedKey]);

  // Open the newest saved sequence for this account once, unless the strategist started editing.
  const drafts = draftsQuery.data?.drafts ?? [];
  useEffect(() => {
    if (loadedFor === row.id || !draftsQuery.isSuccess) return;
    setLoadedFor(row.id);
    if (drafts[0] && !builder.dirty) setBuilder(builderFromDraft(drafts[0]));
  }, [row.id, draftsQuery.isSuccess, drafts, loadedFor, builder.dirty]);

  // Autosave: an edited, unlocked builder with steps saves itself a moment after the last edit,
  // so nothing is lost. A state that cannot be saved (its blocker says why) or just failed waits.
  // It keeps counting while the panel is hidden, so switching away does not drop the edits.
  const autosave = useRef(emptyTracker());
  useEffect(() => {
    const ready = autosaveReady(builder, failedSave);
    autosave.current.pending = ready ? builder : null;
    if (!ready || busy !== null) return;
    const timer = window.setTimeout(() => {
      if (autosave.current.pending === builder) void save(builder, null);
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  });

  // Leaving (unmount, which an account switch is too, or a page unload) saves waiting edits now
  // instead of after the timer. The refs hold what the last render had, so the flush needs no state.
  const leaving = useRef({ getToken, accountId: row.id });
  useEffect(() => { leaving.current = { getToken, accountId: row.id }; });
  useEffect(() => {
    const flushOnLeave = () => { void flushWhileLeaving(); };
    window.addEventListener('beforeunload', flushOnLeave);
    return () => {
      window.removeEventListener('beforeunload', flushOnLeave);
      flushOnLeave();
    };
  }, []);

  const contacts = builderContacts(row.contacts, builder.contactIds);
  const pickedContacts = contacts.filter(contact => builder.contactIds.includes(contact.id));
  const sendableContacts = pickedContacts.filter(contact => contactBlocker(contact) === null);
  // Scout drafts against someone it can greet by name; the server refuses a draft without one.
  const draftableContacts = sendableContacts.filter(contactHasName);
  const days = stepDays(builder.steps);
  const currentDraft = drafts.find(draft => draft.draft_id === builder.draftId) ?? null;
  const missingFields: SequenceMissingField[] = outcome?.missing_fields?.length
    ? outcome.missing_fields : (currentDraft?.missing_fields ?? []);
  const blockers = createBlockers(builder, missingFields);
  const creation: SequenceCreation | null = outcome ?? (currentDraft?.amplemarket_sequence_id ? currentDraft.creation : null);
  const showPersonalLines = copyUsesField(builder.steps, PERSONAL_LINE_FIELD)
    || Object.values(builder.personalLines).some(line => line.trim());

  // The mailbox: the strategist's pick, else Amplemarket's default for them (their own when listed).
  const mailboxes = mailboxesQuery.data ?? null;
  const mailboxOptions = usableMailboxes(mailboxes);
  const mailbox = pickedMailbox && mailboxOptions.includes(pickedMailbox) ? pickedMailbox : (mailboxes?.default_email ?? '');

  // The leaving flush: nothing on screen is left to update, so it only saves and refreshes the list.
  // A failure here reaches nobody but the console: the panel that would show it is gone.
  async function flushWhileLeaving() {
    const { getToken: leavingToken, accountId } = leaving.current;
    try {
      const saved = await flushPendingSave(autosave.current, async state => {
        const reply = await saveSequenceDraft(leavingToken, savePayload(state, accountId), { keepalive: true });
        return reply.draft;
      });
      if (saved !== null) await queryClient.invalidateQueries({ queryKey: ['sequence-drafts', accountId] });
    } catch (error) {
      const reason = panelMessage(error, 'Could not save the draft.');
      console.warn(`sequence draft edits could not be saved on leaving. accountId=${accountId} reason=${reason}`);
    }
  }

  // "New sequence" (the dropdown or the button under a created one) starts from the people
  // ticked in Outreach; a re-tick never replaces a created sequence on its own.
  function openDraft(draft: SequenceDraft | null) {
    setBuilder(draft ? builderFromDraft(draft) : seededBuilder(row.contacts, seedContactIds));
    setOutcome(null);
    setLaunchOutcome(null);
    setMessage(null);
    setDraftNotes({});
  }

  async function connect() {
    setBusy('connect');
    setMessage(null);
    try {
      const started = await startSequenceConnection(getToken);
      window.location.assign(started.authorize_url);
    } catch (error) {
      setMessage({ tone: 'bad', text: panelMessage(error, 'Could not start the Amplemarket connection.') });
      setBusy(null);
    }
  }

  async function disconnect() {
    setBusy('connect');
    try {
      await disconnectSequenceConnection(getToken);
      await queryClient.invalidateQueries({ queryKey: ['sequence-connection'] });
    } catch (error) {
      setMessage({ tone: 'bad', text: panelMessage(error, 'Could not disconnect.') });
    } finally {
      setBusy(null);
    }
  }

  // Re-reads the tools Amplemarket offers this person, so create validates against today's schemas.
  async function refreshTools() {
    setBusy('tools');
    setMessage(null);
    try {
      const refreshed = await refreshSequenceTools(getToken);
      queryClient.setQueryData(['sequence-connection'], refreshed);
      // A newly stored mailbox tool, or a mailbox connected since, shows on the next read.
      await queryClient.invalidateQueries({ queryKey: ['sequence-mailboxes'] });
      const schemaCount = refreshed.schema_tool_names.length;
      setMessage({ tone: 'added', text: `Amplemarket tools refreshed (${schemaCount} create tools read).` });
    } catch (error) {
      setMessage({ tone: 'bad', text: panelMessage(error, 'Could not refresh the Amplemarket tools.') });
    } finally {
      setBusy(null);
    }
  }

  // Saves `state` (the builder by default); the reply carries the server's field check.
  // `savedText` null is the autosave: no "saved" message, and a failure waits for the next edit.
  async function save(state: BuilderState = builder, savedText: string | null = 'Draft saved.') {
    if (personalLinesWithLiquid(state).length) {
      setMessage({ tone: 'bad', text: 'A personal line cannot contain {{ or {%.' });
      return;
    }
    const typedPrefix = unknownPrefix(state.name);
    if (typedPrefix !== null) {
      setMessage({ tone: 'bad', text: `[${typedPrefix}] is not a campaign type. Pick one from the list.` });
      return;
    }
    setBusy('save');
    if (savedText !== null) setMessage(null);
    setOutcome(null);

    // Tracked, so a flush on leaving waits for this save instead of racing it.
    const saving = saveSequenceDraft(getToken, savePayload(state, row.id));
    const tracked = saving.then(reply => reply.draft, () => null);
    trackSave(autosave.current, state, tracked);
    try {
      const saved = await saving;
      setBuilder(current => builderAfterSave(current, state, saved.draft));
      setFailedSave(null);
      await queryClient.invalidateQueries({ queryKey: ['sequence-drafts', row.id] });
      if (savedText !== null) setMessage({ tone: 'added', text: savedText });
    } catch (error) {
      setFailedSave(state);
      setMessage({ tone: 'bad', text: panelMessage(error, 'Could not save the draft.') });
    } finally {
      untrackSave(autosave.current, tracked);
      setBusy(null);
    }
  }

  // Scout writes the step once for everyone ("Draft with Scout"). On the first email the opener
  // comes back as one person's {{personal_line}}, so Scout then writes everyone else's too.
  async function draftStep(stepId: string) {
    if (!draftableContacts.length) return;
    const stepIndex = builder.steps.findIndex(step => step.step_id === stepId);
    setDraftingStepId(stepId);
    setDraftNotes(notes => ({ ...notes, [stepId]: '' }));
    let drafted: Awaited<ReturnType<typeof draftSequenceStep>>;
    try {
      drafted = await draftSequenceStep(getToken, {
        account_id: row.id, contact_ids: sendableContacts.map(contact => contact.id), steps: builder.steps,
        step_index: stepIndex,
      });
    } catch (error) {
      setDraftNotes(notes => ({ ...notes, [stepId]: panelMessage(error, 'Drafting failed.') }));
      setDraftingStepId(null);
      return;
    }
    setDraftingStepId(null);
    if (drafted.declined_reason) {
      setDraftNotes(notes => ({ ...notes, [stepId]: drafted.declined_reason ?? '' }));
      return;
    }

    // The drafted copy, and the drafted-against person's own line, onto the builder as it is now.
    const lineContactId = drafted.personal_line_contact_id;
    const line = drafted.personal_line;
    applyToBuilder(current => {
      const updated = updateStep(current, stepId, {
        body: drafted.body ?? '',
        ...(drafted.subject ? { subject: drafted.subject } : {}),
      });
      return line && lineContactId ? setPersonalLine(updated, lineContactId, line) : updated;
    });
    const note = drafted.rule_note ? `Written once for everyone. ${drafted.rule_note}` : 'Written once for everyone.';
    setDraftNotes(notes => ({ ...notes, [stepId]: note }));

    // The copy now asks for {{personal_line}}: write it for every other picked person Scout can
    // name. A nameless person is flagged beside their name instead, and not retried here.
    const state = latestBuilder.current;
    const stillEmpty = personalLinesStillEmpty(state, draftableContacts.map(contact => contact.id));
    if (copyUsesField(state.steps, PERSONAL_LINE_FIELD) && stillEmpty.length) {
      await fillPersonalLines(stillEmpty, false);
    }
  }

  // Every change from an async step goes through here: applied to the builder as it is when it
  // lands, and mirrored into the ref at once so the next step (and the save) reads it.
  function applyToBuilder(update: (state: BuilderState) => BuilderState) {
    latestBuilder.current = update(latestBuilder.current);
    setBuilder(update);
  }

  // "Fill with Scout" (capped at 10 per click) and the fill after "Draft with Scout" (everyone
  // picked): each missing line is written, the misses are retried once, then the newest builder
  // saves. Anyone still without a line is named, and the field check below offers one click to
  // take {{personal_line}} out of the copy, so Create never ships a template someone cannot fill.
  async function fillPersonalLines(contactIds: string[], capped = true) {
    setBusy('fill');
    setMessage(null);
    const toFill = capped ? personalLinesToFill(contactIds) : contactIds;
    const { failed, notes } = await fillLinesWithRetry(toFill, {
      latest: () => latestBuilder.current,
      apply: applyToBuilder,
      writeLine: async (contactId, steps) => {
        try {
          return await draftSequencePersonalLine(getToken, { account_id: row.id, contact_id: contactId, steps });
        } catch (error) {
          throw new Error(panelMessage(error, 'drafting failed'));
        }
      },
      onWriting: setFillingContactId,
    });
    setBusy(null);

    const failedIds = Object.keys(failed);
    if (failedIds.length) {
      const failedCount = failedIds.length;
      console.warn(`personal lines still unwritten after one retry. accountId=${row.id} failedCount=${failedCount}`);
    }
    await save(latestBuilder.current,
      failedIds.length ? 'Saved. Some lines still need writing.' : 'Personal lines written and saved.');
    const remarks = [
      ...(failedIds.length ? [`No personal line yet for ${failedIds.map(contactId => contactName(row, contactId)).join(', ')}.`
        + ' Write one, or remove {{personal_line}} below.'] : []),
      ...failedIds.map(contactId => `${contactName(row, contactId)}: ${failed[contactId]}`),
      ...Object.entries(notes).map(([contactId, ruleNote]) => `${contactName(row, contactId)}: ${ruleNote}`),
    ];
    if (remarks.length) setMessage({ tone: 'warn', text: remarks.join(' · ') });
  }

  // "Remove the field": out of every step, then saved so the check runs again.
  async function removeFieldFromCopy(field: string) {
    const state = removeField(builder, field);
    setBuilder(state);
    await save(state, `{{${field}}} removed and saved.`);
  }

  // `andLaunch` is the "Create and launch" click: the create goes first, and the launch is offered
  // only after a create that finished in full (or a preview's dry run, whose launch is a dry run too).
  async function createInAmplemarket(settle: { existing_sequence_id?: string; confirm_new_create?: boolean } = {},
                                     andLaunch = false) {
    if (!builder.draftId) return;
    const draftId = builder.draftId;
    const sequenceName = prefixedName(builder.campaignType, builder.name || `${row.account} sequence`);
    setBusy('create');
    setMessage(null);
    setLaunchOutcome(null);
    try {
      const reply = await createSequenceInAmplemarket(getToken, draftId, {
        ...settle, ...(mailbox ? { mailbox } : {}),
      });
      setOutcome(reply.outcome);
      if (reply.outcome.status !== 'dry_run') setBuilder(builderFromDraft(reply.draft));
      if (andLaunch) await launchAfterCreate(draftId, sequenceName, reply.outcome);
      await queryClient.invalidateQueries({ queryKey: ['sequence-drafts', row.id] });
    } catch (error) {
      setMessage({ tone: 'bad', text: panelMessage(error, andLaunch ? 'Could not create and launch the sequence.'
        : 'Could not create the sequence.') });
    } finally {
      setBusy(null);
    }
  }

  // The launch half of "Create and launch": confirmed against what the create actually did, so the
  // count the strategist agrees to is the one the server checks (`leads_added`, not who was picked).
  async function launchAfterCreate(draftId: string, sequenceName: string, created: SequenceCreateOutcome) {
    const createdInFull = created.status === 'created' || created.status === 'dry_run';
    if (!createdInFull) {
      if (created.sequence_id) {
        setMessage({ tone: 'warn', text: 'Not launched: the create did not finish. Fix it, then launch in Amplemarket.' });
      }
      return;
    }

    // Nobody added means nothing to launch; it stays a draft there.
    const leadCount = launchLeadCount(created);
    if (leadCount === 0) {
      setMessage({ tone: 'warn', text: 'Not launched: the create added nobody. It is a draft in Amplemarket.' });
      return;
    }

    // The confirm names who goes in and who was left out. Cancel leaves a created draft.
    const withheldNames = (created.withheld ?? []).map(person => person.name);
    if (!window.confirm(launchConfirmText(sequenceName, leadCount, withheldNames))) {
      const text = created.status === 'dry_run' ? 'Preview: not launched.'
        : 'Created as a draft in Amplemarket, not launched. Launch it there when you are ready.';
      setMessage({ tone: 'warn', text });
      return;
    }

    // The one launch call, with the count just confirmed. The create already stands if it fails.
    setBusy('launch');
    try {
      const launched = await launchSequenceInAmplemarket(getToken, draftId, leadCount);
      setLaunchOutcome(launched.outcome);
      if (launched.outcome.status !== 'dry_run') setBuilder(builderFromDraft(launched.draft));
    } catch (error) {
      // The request may have reached Amplemarket before it failed, so never say it was not sent.
      setMessage({ tone: 'warn', text: panelMessage(error, 'The launch request failed.')
        + ' The launch may have been sent — check Amplemarket before trying again.' });

      // Re-read the draft, so the recorded launch status (launching, failed, unknown) shows.
      await queryClient.invalidateQueries({ queryKey: ['sequence-drafts', row.id] });
      const refreshed = queryClient.getQueryData<{ drafts: SequenceDraft[] }>(['sequence-drafts', row.id]);
      const refreshedDraft = refreshed?.drafts.find(draft => draft.draft_id === draftId);
      if (refreshedDraft) setBuilder(builderFromDraft(refreshedDraft));
    }
  }

  const noMailbox = Boolean(mailboxes?.refusal) && !isDryRun;
  const canCreate = blockers.length === 0 && (isDryRun || Boolean(connection?.connected)) && busy === null && !noMailbox;
  const isRetry = currentDraft?.status === 'partial';
  const outcomeUnknown = createOutcomeUnknown(currentDraft) || outcome?.status === 'needs_check';
  const beingCreated = currentDraft?.status === 'creating';
  // While a create or launch is out, the builder takes no edits: its reply replaces the builder,
  // which would silently drop anything typed meanwhile.
  const editingPaused = builder.locked || busy === 'create' || busy === 'launch';

  return (
    <>
      <ConnectionBlock connection={connection} loading={connectionQuery.isPending} busy={busy === 'connect'}
        refreshing={busy === 'tools'} onConnect={connect} onDisconnect={disconnect} onRefreshTools={refreshTools} />

      <SideBlock heading="Sequence" hint={currentDraft ? statusLabel(currentDraft) : null}>
        {/* Which saved draft is open sits on its own row, above the fields that shape the sequence, so
            the two dropdowns never read as one control. */}
        {drafts.length ? (
          <div className="seqrow">
            <label className="seqfield">
              <span className="seqfieldlabel">Draft</span>
              <select value={builder.draftId ?? ''} disabled={busy === 'create' || busy === 'launch'}
                onChange={event => openDraft(drafts.find(draft => draft.draft_id === event.target.value) ?? null)}>
                <option value="">New sequence</option>
                {drafts.map(draft => <option key={draft.draft_id} value={draft.draft_id}>{draft.name}</option>)}
              </select>
            </label>
          </div>
        ) : null}
        <div className="seqrow">
          {/* The campaign-type prefix ("[Fundraise] …") that rolls the sequence up into Salesforce journeys. */}
          <label className="seqfield">
            <span className="seqfieldlabel">Campaign type</span>
            <select value={builder.campaignType} disabled={editingPaused}
              onChange={event => setBuilder(state => setCampaignType(state, event.target.value))}>
              {CAMPAIGN_TYPE_GROUPS.map(entry => (
                <optgroup key={entry.group} label={entry.group}>
                  {entry.types.map(type => <option key={type} value={type}>{type}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="seqfield seqname">
            <span className="seqfieldlabel">Name</span>
            <input placeholder={`${row.account} sequence`}
              value={builder.name} disabled={editingPaused} maxLength={200}
              onChange={event => setBuilder(state => renameBuilder(state, event.target.value))} />
          </label>
        </div>
        <p className="seqhint">Created as “{prefixedName(builder.campaignType, builder.name || `${row.account} sequence`)}”</p>
        {currentDraft?.impersonator_email ? (
          <p className="seqhint">Saved by {currentDraft.impersonator_email}</p>
        ) : null}
      </SideBlock>

      <SideBlock heading="Contacts" hint={`${builder.contactIds.length} picked`}>
        {contacts.length === 0 ? <p className="prose">No people on this account yet.</p> : (
          <div className="seqcontacts">
            {contacts.map(contact => {
              const blocker = contactBlocker(contact);
              const picked = builder.contactIds.includes(contact.id);
              const display = contactDisplay(contact.name, contact.email, 'Unnamed', contact);
              const nameHint = contactNameHint(contact);

              // A shared inbox is only here because a draft or the seed picked it: say so, offer Remove.
              if (isSharedInbox(contact.email)) {
                return (
                  <div key={contact.id} className="seqcontact sharedinbox">
                    <span className="pname unnamed">{contact.email}</span>
                    <span className="seqchip">shared inbox</span>
                    <button type="button" className="seqremove" disabled={editingPaused}
                      aria-label={`Remove ${contact.email}`}
                      onClick={() => setBuilder(state => toggleContact(state, contact.id))}>Remove</button>
                  </div>
                );
              }
              return (
                <label key={contact.id} className={blocker ? 'seqcontact blocked' : 'seqcontact'}>
                  <input type="checkbox" checked={picked} disabled={editingPaused || (Boolean(blocker) && !picked)}
                    onChange={() => setBuilder(state => toggleContact(state, contact.id))} />
                  <span className={display.named ? 'pname' : 'pname unnamed'}>{display.text}</span>
                  {contact.title ? <span className="ptitle">{contact.title}</span> : null}
                  {contact.hero_rank ? <span className="seqchip">#{contact.hero_rank}</span> : null}
                  {blocker ? <span className="seqhint">{blocker}</span> : null}
                  {picked && nameHint ? <span className="seqhint">{nameHint}</span> : null}
                </label>
              );
            })}
          </div>
        )}
      </SideBlock>

      <SideBlock heading="Steps" hint={builder.steps.length ? `${builder.steps.length} · ${days[days.length - 1]} days` : null}>
        {builder.steps.map((step, index) => (
          <SequenceStepCard key={step.step_id} step={step} position={index + 1} day={days[index] ?? 0}
            isFirst={index === 0} isLast={index === builder.steps.length - 1} locked={builder.locked}
            paused={editingPaused}
            emailModeSupported={Boolean(connection?.email_mode_supported)}
            drafting={draftingStepId === step.step_id} draftNote={draftNotes[step.step_id] || null}
            canDraft={draftableContacts.length > 0 && draftingStepId === null && busy === null}
            onEdit={edit => setBuilder(state => updateStep(state, step.step_id, edit))}
            onMove={direction => setBuilder(state => moveStep(state, step.step_id, direction))}
            onRemove={() => setBuilder(state => removeStep(state, step.step_id))}
            onDraft={() => draftStep(step.step_id)} />
        ))}
        {builder.locked ? null : <AddStep disabled={editingPaused} onAdd={type => setBuilder(state => addStep(state, type))} />}
        {!builder.locked && builder.steps.some(step => step.body.includes('{{')) ? (
          <p className="seqhint">{'{{first_name}}, {{company_name}} and {{personal_line}} fill in per person.'}</p>
        ) : null}
      </SideBlock>

      {showPersonalLines ? (
        <SideBlock heading="Personal lines" hint={`${sendableContacts.filter(contact => builder.personalLines[contact.id]?.trim()).length} of ${sendableContacts.length}`}>
          <SequencePersonalLines contacts={sendableContacts} lines={builder.personalLines} locked={editingPaused}
            filling={fillingContactId}
            onEdit={(contactId, text) => setBuilder(state => setPersonalLine(state, contactId, text))} />
        </SideBlock>
      ) : null}

      {connection?.connected && !isDryRun && !builder.locked ? (
        <SequenceMailboxPicker mailboxes={mailboxes} loading={mailboxesQuery.isPending} value={mailbox}
          disabled={busy !== null} onChange={setPickedMailbox} />
      ) : null}

      {!builder.dirty && !builder.locked ? (
        <SequenceFieldCheck missing={missingFields} steps={builder.steps} busy={busy !== null} onFill={fillPersonalLines}
          onRemove={removeFieldFromCopy} />
      ) : null}

      <div className="pushbar">
        <span className="pushcount">{blockers.length && !builder.locked ? blockers[0] : ''}</span>
        {builder.locked ? (
          <button type="button" className="pushchip" onClick={() => openDraft(null)}>New sequence</button>
        ) : failedSave !== null && builder.dirty ? (
          <button type="button" className="pushchip" disabled={busy !== null} onClick={() => save(builder, null)}>
            Retry save
          </button>
        ) : null}
        {currentDraft?.status === 'created' || outcomeUnknown ? null : beingCreated ? (
          <span className="seqhint">Being created…</span>
        ) : isRetry ? (
          <button type="button" className="pushchip primary" disabled={!canCreate} onClick={() => createInAmplemarket()}>
            {busy === 'create' ? 'Creating…' : 'Retry'}
          </button>
        ) : builder.locked ? null : (
          <>
            <button type="button" className="pushchip" disabled={!canCreate} onClick={() => createInAmplemarket()}>
              {busy === 'create' ? 'Creating…' : 'Create draft in Amplemarket'}
            </button>
            <button type="button" className="pushchip primary" disabled={!canCreate}
              onClick={() => createInAmplemarket({}, true)}>
              {busy === 'launch' ? 'Launching…' : 'Create and launch in Amplemarket'}
            </button>
          </>
        )}
      </div>

      {message ? <div className={`pushresult ${message.tone}`}>{message.text}</div> : null}
      {outcomeUnknown ? (
        <UnknownOutcome name={prefixedName(builder.campaignType, builder.name)} busy={busy === 'create'}
          onExisting={sequenceId => createInAmplemarket({ existing_sequence_id: sequenceId })}
          onConfirmNew={() => createInAmplemarket({ confirm_new_create: true })} />
      ) : creation ? (
        <CreationResult creation={creation} outcome={outcome} launchOutcome={launchOutcome} contactNames={contactNames(row)}
          sequenceExists={Boolean(outcome ? outcome.sequence_id : currentDraft?.amplemarket_sequence_id)} />
      ) : null}
    </>
  );
}

function contactName(row: BookAccount, contactId: string): string {
  return contactNames(row)[contactId] ?? contactId;
}

function contactNames(row: BookAccount): Record<string, string> {
  const names: Record<string, string> = {};
  row.contacts.forEach(contact => { names[contact.id] = contactDisplayName(contact.name, contact.email, contact.id); });
  return names;
}

function statusLabel(draft: SequenceDraft): string {
  if (draft.status === 'created') return 'in Amplemarket';
  if (draft.status === 'creating') return 'creating';
  if (createOutcomeUnknown(draft)) return 'check Amplemarket';
  if (draft.status === 'partial') return 'partly created';
  return 'saved';
}

function ConnectionBlock({ connection, loading, busy, refreshing, onConnect, onDisconnect, onRefreshTools }: {
  connection: SequenceConnection | null;
  loading: boolean;
  busy: boolean;
  refreshing: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onRefreshTools: () => void;
}) {
  if (loading || !connection) return null;
  const previewNote = previewCreateNote(createMode(connection));
  return (
    <SideBlock heading="Amplemarket" hint={connection.preview ? 'preview' : null}>
      {connection.connected ? (
        <div className="seqrow">
          <span className="seqconnected">Connected</span>
          <button type="button" className="brieftoggle" disabled={busy || refreshing} onClick={onRefreshTools}>
            {refreshing ? 'Refreshing…' : 'Refresh Amplemarket tools'}
          </button>
          <button type="button" className="brieftoggle" disabled={busy} onClick={onDisconnect}>Disconnect</button>
        </div>
      ) : connection.may_connect ? (
        <div className="seqrow">
          <span className="seqhint">{connection.status === 'needs_reconnect' ? 'Your connection expired.' : 'Not connected.'}</span>
          <button type="button" className="pushchip" disabled={busy} onClick={onConnect}>
            {busy ? 'Opening…' : 'Connect Amplemarket'}
          </button>
        </div>
      ) : (
        <p className="seqhint">{connection.unavailable_reason}</p>
      )}
      {connection.connected && !connection.schema_tool_names?.length ? (
        <p className="seqhint">Refresh Amplemarket tools before creating: no tool schemas are stored yet.</p>
      ) : null}
      {previewNote ? <p className="seqhint">{previewNote}</p> : null}
    </SideBlock>
  );
}

function AddStep({ disabled, onAdd }: { disabled: boolean; onAdd: (type: SequenceStepType) => void }) {
  const [type, setType] = useState<SequenceStepType>('email');
  return (
    <div className="seqrow seqadd">
      <select aria-label="New step type" value={type} disabled={disabled}
        onChange={event => setType(event.target.value as SequenceStepType)}>
        {STEP_TYPES.map(option => <option key={option.type} value={option.type}>{option.label}</option>)}
      </select>
      <button type="button" className="pushchip" disabled={disabled} onClick={() => onAdd(type)}>Add step</button>
    </div>
  );
}

function CreationResult({ creation, outcome, launchOutcome, contactNames, sequenceExists }: {
  creation: SequenceCreation;
  outcome: SequenceCreateOutcome | null;
  launchOutcome: SequenceLaunchOutcome | null;
  contactNames: Record<string, string>;
  sequenceExists: boolean;
}) {
  // A dry run lists the calls a create would make, and says nothing was sent.
  if (outcome?.status === 'dry_run') {
    return (
      <div className="pushresult">
        <strong>Preview: nothing was created.</strong> A create would:
        <ol className="seqplan">{outcome.planned.map((call, index) => <li key={index}>{call.summary}</li>)}</ol>
        {launchOutcome ? <div>{launchHeadline(launchOutcome).text}</div> : null}
        <PeopleList heading="Left out" people={creation.withheld ?? []} />
      </div>
    );
  }
  // Stopped before anything went out: the reason alone. Missing fields have their own block.
  if (outcome?.status === 'refused') {
    if (outcome.missing_fields?.length) return null;
    return <div className="pushresult bad">{outcome.error}</div>;
  }

  // Whether a sequence exists decides the sentence; "partly" is only for one that does.
  const headline = creationHeadline(creation, outcome, sequenceExists);
  // How a launch went: this click's reply, else the record on the draft.
  const launch = launchOutcome ?? creation.launch_record ?? null;
  const launchLine = launch ? launchHeadline(launch) : null;
  const leadFailures = creation.lead_failures ?? [];
  const addedNames = (creation.leads_added ?? []).map(contactId => contactNames[contactId] ?? contactId);
  return (
    <div className={`pushresult ${headline.tone}`}>
      <div>{headline.text}</div>
      {launchLine ? <div className={launchLine.tone === 'added' ? '' : 'seqfailure'}>{launchLine.text}</div> : null}
      {creation.error ? <div className="seqfailure">{creation.error}</div> : null}
      {sequenceExists && creation.mailbox ? <div className="seqhint">Sends from {creation.mailbox}</div> : null}
      {sequenceExists && addedNames.length ? <div className="seqhint">Added: {addedNames.join(', ')}</div> : null}
      <PeopleList heading="Not added" people={leadFailures} />
      <PeopleList heading="Left out" people={creation.withheld ?? []} />
      {sequenceExists && creation.sequence_url ? (
        <a className="seqopen" href={creation.sequence_url} target="_blank" rel="noopener">
          {launch?.status === 'launched' ? 'Open in Amplemarket →' : 'Open in Amplemarket to launch →'}
        </a>
      ) : null}
    </div>
  );
}

function PeopleList({ heading, people }: { heading: string; people: { contact_id: string; name: string; reason: string }[] }) {
  if (!people.length) return null;
  return (
    <div className="pushrows">
      <span className="seqhint">{heading}</span>
      {people.map(person => (
        <div className="pushrowresult" key={person.contact_id}>
          <span className="pushwho">{person.name}</span>
          <span>{person.reason}</span>
        </div>
      ))}
    </div>
  );
}

// The last create went out and no answer came back. Creating again could make a duplicate, so
// the strategist looks in Amplemarket first and says which it is.
function UnknownOutcome({ name, busy, onExisting, onConfirmNew }: {
  name: string;
  busy: boolean;
  onExisting: (sequenceId: string) => void;
  onConfirmNew: () => void;
}) {
  const [sequenceId, setSequenceId] = useState('');
  return (
    <div className="pushresult warn">
      <div>The last create may have reached Amplemarket. Look there for “{name}” before going on.</div>
      <div className="seqrow">
        <input aria-label="Amplemarket sequence id" placeholder="Sequence id from Amplemarket" value={sequenceId}
          onChange={event => setSequenceId(event.target.value)} />
        <button type="button" className="pushchip" disabled={busy || !sequenceId.trim()}
          onClick={() => onExisting(sequenceId.trim())}>Use this sequence</button>
      </div>
      <div className="seqrow">
        <button type="button" className="brieftoggle" disabled={busy} onClick={onConfirmNew}>
          It isn’t there. Create it again
        </button>
      </div>
    </div>
  );
}
